import { useState, useEffect } from 'react';
import { useLanguage } from '@/context/LanguageContext';
import { Menu, X, Globe } from 'lucide-react';

const NAV_KEYS = ['about', 'gallery', 'distribution', 'location', 'contact'];
const SECTION_IDS = ['about', 'gallery', 'floors', 'location', 'contact'];

export default function Header() {
  const { lang, toggleLang, t } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMobileOpen(false);
  };

  return (
    <header
      data-testid="main-header"
      className={`fixed top-0 w-full z-50 transition-all duration-500 ${
        scrolled
          ? 'backdrop-blur-xl bg-[#050505]/80 border-b border-white/5'
          : 'bg-transparent'
      }`}
    >
      <div className="px-6 md:px-12 lg:px-24 max-w-[1600px] mx-auto flex items-center justify-between h-16 md:h-20">
        <button
          onClick={() => scrollTo('hero')}
          className="font-heading text-lg md:text-xl tracking-tight text-[#F5F5F0] hover:text-[#C8B598] transition-colors"
          data-testid="logo-button"
        >
          Villa<span className="text-[#C8B598]">259</span><span className="text-xs text-[#9CA3AF] ml-1">.com</span>
        </button>

        <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
          {NAV_KEYS.map((key, i) => (
            <button
              key={key}
              onClick={() => scrollTo(SECTION_IDS[i])}
              className="text-xs font-body uppercase tracking-[0.2em] text-[#9CA3AF] hover:text-[#C8B598] transition-colors"
              data-testid={`nav-${key}`}
            >
              {t(`nav.${key}`)}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={toggleLang}
            className="flex items-center gap-1.5 px-3 py-1.5 border border-white/10 text-xs uppercase tracking-widest text-[#9CA3AF] hover:text-[#C8B598] hover:border-[#C8B598]/30 transition-all"
            data-testid="language-switcher"
          >
            <Globe className="w-3.5 h-3.5" />
            {lang === 'en' ? 'ES' : 'EN'}
          </button>
          <button
            onClick={() => scrollTo('contact')}
            className="hidden md:inline-flex items-center px-5 py-2 bg-[#C8B598] text-[#050505] text-xs font-medium uppercase tracking-wider hover:bg-[#E2D1B8] transition-all"
            data-testid="nav-cta-button"
          >
            {t('nav.cta')}
          </button>
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden text-[#F5F5F0]"
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-[#050505]/95 backdrop-blur-xl border-t border-white/5 px-6 py-6 space-y-4 animate-slideDown" data-testid="mobile-menu">
          {NAV_KEYS.map((key, i) => (
            <button
              key={key}
              onClick={() => scrollTo(SECTION_IDS[i])}
              className="block w-full text-left text-sm uppercase tracking-widest text-[#9CA3AF] hover:text-[#C8B598] transition-colors py-2"
              data-testid={`mobile-nav-${key}`}
            >
              {t(`nav.${key}`)}
            </button>
          ))}
          <button
            onClick={() => scrollTo('contact')}
            className="w-full mt-2 px-5 py-3 bg-[#C8B598] text-[#050505] text-xs font-medium uppercase tracking-wider"
            data-testid="mobile-cta-button"
          >
            {t('nav.cta')}
          </button>
        </div>
      )}
    </header>
  );
}
